import React from "react";
import { t } from "ttag";

import type {
    ServerCmsTypesCategoryProductFacet as CategoryProductFacet,
    Money,
    ServerCmsTypesProductSummary as ProductSummary,
    ProductGalleryBlock as Value,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Themed } from "../../styles/themes";
import { Carousel } from "../Carousel/Carousel";
import { ButtonPrimary } from "../Clickables";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SKSArrows } from "../SKSCarousel/Arrows";
import { GalleryModal } from "./GalleryModal";

import breakpoint from "../../styles/breakpoint.module.scss";
import styles from "./index.module.scss";

const MAX_FEATURED_FACETS = 3;

const carouselImageSizes = [
    `(min-width: ${breakpoint.laptop}) 33vw`,
    `(min-width: ${breakpoint.tablet}) 50vw`,
    `100vw`,
].join(", ");

export const ProductGallery = (
    props: Readonly<{
        value: Value;
        featured_facets: CategoryProductFacet[];
        msrp: Money | null;
        status: ProductSummary["status"];
    }>,
) => {
    const isDiscontinued = props.status === "discontinued";
    const showPrice = props.msrp !== null && !isDiscontinued;

    const facets = props.featured_facets.slice(0, MAX_FEATURED_FACETS);

    return (
        <Themed tag="section" theme={props.value.theme}>
            <div className={styles.inner}>
                <header className={styles.header}>
                    {props.value.content && (
                        <RichTextStreamBlock value={props.value.content} />
                    )}
                    {props.value.gallery_images &&
                        props.value.gallery_images.length > 0 && (
                            <GalleryModal
                                images={props.value.gallery_images}
                                trigger={
                                    <ButtonPrimary
                                        className={styles.galleryButton}
                                    >{t`View Gallery`}</ButtonPrimary>
                                }
                            />
                        )}
                </header>

                {props.value.carousel_images &&
                    props.value.carousel_images.length > 0 && (
                        <div className={styles.carouselArea}>
                            <div className={styles.carouselInner}>
                                <Carousel
                                    label={t`Product images`}
                                    loop={false}
                                    slideContainerProps={{
                                        className: styles.slide,
                                    }}
                                    controls={({ allVisible }) => (
                                        <SKSArrows
                                            allVisible={allVisible}
                                            className={styles.controls}
                                        />
                                    )}
                                    dots={null}
                                >
                                    {props.value.carousel_images.map(
                                        (image, index) => (
                                            <ImageChooserBlock
                                                key={index}
                                                value={image}
                                                sizes={carouselImageSizes}
                                            />
                                        ),
                                    )}
                                </Carousel>
                            </div>
                        </div>
                    )}

                {(facets.length > 0 || showPrice) && (
                    <dl className={styles.facets}>
                        {facets.map((facet) => (
                            <div
                                className={styles.facet}
                                key={`${facet.facet_slug}-${facet.value_slug}`}
                            >
                                <dt className={styles.facetLabel}>
                                    {facet.name}
                                </dt>
                                <dd className={styles.facetValue}>
                                    {facet.display_value}
                                </dd>
                            </div>
                        ))}
                        {showPrice && props.msrp && (
                            <div className={styles.facet}>
                                <dt
                                    className={styles.facetLabel}
                                >{t`Price`}</dt>
                                <dd className={styles.facetValue}>
                                    {props.msrp.formatted}
                                </dd>
                            </div>
                        )}
                    </dl>
                )}
            </div>
        </Themed>
    );
};
